import React, { useState } from "react";
import classes from "./ProgramDropDown.module.css";

/* Image assets exported from Canva (with blue circle baked in) */
import DailyTherapiesImg from "../../assets/img/header/Daily Therapies & Treatments.png";
import EducationSupportImg from "../../assets/img/header/Education & Support.png";
import WellnessMonitoringImg from "../../assets/img/header/Wellness Monitoring.png";
import AmityWelcomePackImg from "../../assets/img/header/Amity Welcome Pack.png";
import NutritionDetoxSupportImg from "../../assets/img/header/Nutrition and Detox Botanical Support.png";
import CleansingNutritionImg from "../../assets/img/header/Cleansing Nutrition.png";

const ProgramDropDown = ({ stickyOffset = 80 }) => {
  const [openSections, setOpenSections] = useState([]);

  const isOpen = (id) => openSections.includes(id);

  const toggle = (id, e) => {
    e?.preventDefault();
    const btn =
      e?.currentTarget ||
      document.querySelector(`[aria-controls="amityProgram_${id}"]`);
    const startTop = btn ? btn.getBoundingClientRect().top : null;

    setOpenSections((prev) => {
      const willOpen = !prev.includes(id);
      const next = willOpen ? [...prev, id] : prev.filter((s) => s !== id);
      if (willOpen) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (btn && startTop !== null) {
              const endTop = btn.getBoundingClientRect().top;
              const delta = endTop - startTop;
              if (Math.abs(delta) > 1) {
                window.scrollBy({ top: delta, left: 0 });
              }
              btn.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        });
      }
      return next;
    });
  };

  return (
    <section
      className={classes.amityProgram_root}
      style={{ "--amityProgram_stickyOffset": `${stickyOffset}px` }}
    >
      {/* Wellness Programs */}
      <div className={classes.amityProgram_block}>
        <button
          className={classes.amityProgram_toggle}
          type="button"
          aria-expanded={isOpen("programs")}
          aria-controls="amityProgram_programs"
          onClick={(e) => toggle("programs", e)}
        >
          <span className={classes.amityProgram_toggleLabel}>
            Our All-Inclusive Wellness Programs
          </span>
          <span className={classes.amityProgram_icon}>
            {isOpen("programs") ? "−" : "+"}
          </span>
        </button>

        {isOpen("programs") && (
          <div
            id="amityProgram_programs"
            className={classes.amityProgram_content}
            role="region"
            aria-label="Wellness Programs"
          >
            <p className={classes.amityProgram_text}>
              There’s no need to decide in advance—you can choose one program,
              combine programs, or adjust during your stay (subject to pre-screening
              and consultation).
            </p>

            <p className={classes.amityProgram_text}>
              Every program includes luxury accommodation, is supported by targeted
              detox protocols, and comes with the full support of our experienced
              cleansing specialists.
            </p>

            <ul className={classes.amityProgram_list}>
              <li className={classes.amityProgram_item}>
                <div className={classes.amityProgram_itemTitle}>Full Fast</div>
                <div>
                  Our most intensive detox. Structured fasting supported by cleansing
                  botanicals and advanced detox protocols. Pre-cleanse required.
                </div>
              </li>

              <li className={classes.amityProgram_item}>
                <div className={classes.amityProgram_itemTitle}>Juice Cleanse</div>
                <div>
                  Full Fast framework with added cold-pressed juices for energy and
                  micronutrients, combined with powerful detox protocols.
                </div>
              </li>

              <li className={classes.amityProgram_item}>
                <div className={classes.amityProgram_itemTitle}>Intermittent Fasting</div>
                <div>
                  A daily fasting window (typically 16 hours) paired with a smoothie and
                  two nourishing plant-based meals. Can be adjusted for experienced
                  fasters. Supported by detox protocols.
                </div>
              </li>

              <li className={classes.amityProgram_item}>
                <div className={classes.amityProgram_itemTitle}>Raw Cleanse</div>
                <div>
                  A fully plant-based raw menu (breakfast, lunch, dinner, desserts,
                  juices/smoothies) combined with targeted detox protocols. Classic Raw,
                  Low-Glycemic, Candida, Thyroid, and Low-FODMAP options available.
                </div>
              </li>
            </ul>
          </div>
        )}
      </div> {/* <-- ✅ close the first block */}

      {/* Inclusions */}
      <div className={classes.amityProgram_block}>
        <button
          className={classes.amityProgram_toggle}
          type="button"
          aria-expanded={isOpen("inclusions")}
          aria-controls="amityProgram_inclusions"
          onClick={(e) => toggle("inclusions", e)}
        >
          <span className={classes.amityProgram_toggleLabel}>
            Cleansing Program Inclusions
          </span>
          <span className={classes.amityProgram_icon}>
            {isOpen("inclusions") ? "−" : "+"}
          </span>
        </button>

        {isOpen("inclusions") && (
          <div
            id="amityProgram_inclusions"
            className={classes.amityProgram_content}
            role="region"
            aria-label="Cleansing Program Inclusions"
          >
            <p className={classes.amityProgram_text}>
              Every Amity Wellness Cleansing Program includes luxury accommodation, transformative
              wellness program, cleansing botanicals, powerful detox protocols, full use
              of the onsite facilities (including two swimming pools), and full support
              from our cleansing specialists.
            </p>

            {/* 1) Daily Therapies & Treatments */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={DailyTherapiesImg}
                  alt="Daily Therapies & Treatments"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>
                Daily Therapies & Treatments
              </h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Daily infused Colon Cleanse</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Inflammation Pod (FIR Infrared technology) with Oxygen Therapy</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Restorative Massage</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Thai Herbal Sauna</span></li>
                <li className={classes.amityInclusions_item}><span>Ice bath (3 evenings per week)</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Beach Trip</span></li>
                <li className={classes.amityInclusions_item}><span>All Group Yoga Classes</span></li>
                <li className={classes.amityInclusions_item}><span>Twice daily group wellness Activities</span></li>
                <li className={classes.amityInclusions_item}><span>All Guided Nature Walks</span></li>
              </ul>
            </div>

            {/* 6) Cleansing Nutrition */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={CleansingNutritionImg}
                  alt="Cleansing Nutrition"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>Cleansing Nutrition</h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Welcome Salad</span></li>
                <li className={classes.amityInclusions_item}><span>Fresh Coconuts</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Superfood Shot</span></li>
                <li className={classes.amityInclusions_item}><span>Organic Cold pressed Coconut Oil</span></li>
                <li className={classes.amityInclusions_item}><span>Raw Vegan Meals (Breakfast, Lunch, Dinner, Dessert)</span></li>
                <li className={classes.amityInclusions_item}><span>Cold Pressed Juices and Nourishing Smoothies</span></li>
                <li className={classes.amityInclusions_item}><span>Detox Tonic Shot</span></li>
                <li className={classes.amityInclusions_item}><span>Cleansing Botanicals</span></li>
                <li className={classes.amityInclusions_item}><span>Post-Cleanse Vegan Breakfast</span></li>
                <li className={classes.amityInclusions_item}><span>Post-Cleanse access to the support group</span></li>
              </ul>
            </div>

            {/* 4) Amity Welcome Pack */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={AmityWelcomePackImg}
                  alt="Amity Welcome Pack"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>Amity Welcome Pack</h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Amity Yoga Pants</span></li>
                <li className={classes.amityInclusions_item}><span>Amity Training Top</span></li>
                <li className={classes.amityInclusions_item}><span>Canvas Shoulder Bag</span></li>
                <li className={classes.amityInclusions_item}><span>Detox Shaker</span></li>
                <li className={classes.amityInclusions_item}><span>BPA-Free Water Bottle</span></li>
                <li className={classes.amityInclusions_item}><span>Holistic In-room First Aid Kit</span></li>
              </ul>
            </div>

            {/* 5) Nutrition and Detox Botanical Support */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={NutritionDetoxSupportImg}
                  alt="Nutrition and Detox Botanical Support"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>
                Nutrition and Detox Botanical Support
              </h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Cleansing Drinks</span></li>
                <li className={classes.amityInclusions_item}><span>Chelation Supplements</span></li>
                <li className={classes.amityInclusions_item}><span>Liver Cleansing Supplements</span></li>
                <li className={classes.amityInclusions_item}><span>Organic Coconut Oil</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Superfood Shot</span></li>
                <li className={classes.amityInclusions_item}><span>Vegan Mineral or Bone Broth</span></li>
                <li className={classes.amityInclusions_item}><span>Probiotics during and after program</span></li>
                <li className={classes.amityInclusions_item}><span>Probiotic Re-Inoculation Implant</span></li>
                <li className={classes.amityInclusions_item}><span>Herbal Teas in Room</span></li>
              </ul>
            </div>

            {/* 2) Education & Support */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={EducationSupportImg}
                  alt="Education & Support"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>Education & Support</h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Pre-Screening Assessment</span></li>
                <li className={classes.amityInclusions_item}><span>Full Support from our Wellness Team</span></li>
                <li className={classes.amityInclusions_item}><span>Wellness Program Orientation</span></li>
                <li className={classes.amityInclusions_item}><span>All Educational Talks</span></li>
                <li className={classes.amityInclusions_item}><span>All Raw Food Workshops and Demonstrations</span></li>
                <li className={classes.amityInclusions_item}><span>Access to Private Additional Therapies (Additional Cost)</span></li>
                <li className={classes.amityInclusions_item}><span>Amity Wellness Handbook</span></li>
                <li className={classes.amityInclusions_item}><span>Amity Private support group access</span></li>
              </ul>
            </div>

            {/* 3) Wellness Monitoring */}
            <div className={classes.amityProgram_group}>
              <div className={classes.amityProgram_groupIcon}>
                <img
                  src={WellnessMonitoringImg}
                  alt="Wellness Monitoring"
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <h3 className={classes.amityProgram_groupTitle}>Wellness Monitoring</h3>
              <ul className={classes.amityInclusions_list}>
                <li className={classes.amityInclusions_item}><span>Toxicity Questionnaire Report</span></li>
                <li className={classes.amityInclusions_item}><span>Vitamin Deficiency Questionnaire Report</span></li>
                <li className={classes.amityInclusions_item}><span>Body Measurements</span></li>
                <li className={classes.amityInclusions_item}><span>Before/After Visceral Fat Testing</span></li>
                <li className={classes.amityInclusions_item}><span>Daily Blood Pressure Checks</span></li>
                <li className={classes.amityInclusions_item}><span>Saliva pH Testing prior to program</span></li>
              </ul>
            </div>

            {/* Footer note */}
            <p className={classes.amityProgram_footer}>
              ❤️ Every detail is designed to support, guide, and empower you through your
              cleansing journey — while ensuring safety, comfort, and long-lasting results.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProgramDropDown;
